import {
    useCallback,
    useMemo,
} from 'react'

import {
    useNavigate,
} from 'react-router-dom'

import SkyNetDataGridExpandable
    from 'app/shared-components/SkyNetDataGridExpandable/SkyNetDataGridExpandable'
import useDataGridSource from 'app/shared-components/SkyNetMasterDataGrid/hooks/useDataGridSource'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import longTermLeaseInvoicesRequests from 'app/requests/LongTermLease/LongTermLeaseInvoices.request'
import {
    DataGridSupportedFeatures,
} from 'app/shared-components/SkyNetMasterDataGrid'
import {
    PersistentKeys,
    registerPersistentKey,
} from 'app/enums/persistentKeys'
import useLabel from 'app/hooks/useLabel'
import {
    useSkyNetRoutes,
} from 'app/SkyNetRoutes'
import {
    CommonRoutes,
} from 'app/enums/common.enums'

import {
    LongTermLeaseType,
} from '../longTermLease.types'
import {
    LongTermLeaseInvoicesContext,
} from './hooks/useLongTermLeaseInvoicesContext'
import LongTermLeaseInvoicesCreate from './LongTermLeaseInvoicesCreate'
import LongTermLeaseInvoicesUpdate from './LongTermLeaseInvoicesUpdate'
import columns from './LongTermLeaseInvoices.columns'

type Props = {
    data: LongTermLeaseType,
}

const toolbarControls = [DataGridSupportedFeatures.ShowArchived]
const persistentKey = registerPersistentKey(PersistentKeys.LongTermLeaseInvoicesTable)

const LongTermLeaseInvoices = ({
    data,
}: Readonly<Props>) => {
    const l = useLabel()
    const navigate = useNavigate()

    const request = useMemo(() => {
        return longTermLeaseInvoicesRequests.filterRequestAll({
            longTermLeaseId: data?.id,
        })
    }, [data?.id])

    const dataSource = useDataGridSource({
        request,
        key: RequestKeys.getLongTermLeaseInvoices,
    })

    const longTermLeaseInvoicesContext = useMemo(() => {
        return {
            longTermLease: data,
        }
    }, [data])

    const onRowClick = useCallback(({
        id,
    }) => {
        navigate(`./${id}`)
    }, [navigate])

    return (
        <LongTermLeaseInvoicesContext.Provider value={longTermLeaseInvoicesContext}>
            {useSkyNetRoutes({
                routes: [
                    {
                        path: CommonRoutes.Slash,
                        element: (
                            <SkyNetDataGridExpandable
                                persistentKey={persistentKey}
                                columns={columns}
                                customDataSource={dataSource}
                                toolbarProps={{
                                    title: l('INVOICES'),
                                    toolbarControls: [],
                                }}
                                featuresLists={toolbarControls}
                                onRowClick={onRowClick}
                                ActionComponents={{
                                    Create: {
                                        component: LongTermLeaseInvoicesCreate,
                                    },
                                }}
                            />
                        ),
                    },
                    {
                        path: ':invoiceId',
                        element: <LongTermLeaseInvoicesUpdate />,
                    },
                ],
            })}
        </LongTermLeaseInvoicesContext.Provider>
    )
}

export default LongTermLeaseInvoices
